import * as THREE from 'three';

/*
 * StarTrails — animated warp streaks flying past the ship.
 *
 * Design goal: a continuous stream of fading star trails with (almost) zero
 * CPU cost per frame.
 *
 * How it works
 * ------------
 * A single THREE.LineSegments object holds one line per trail: a bright "head"
 * vertex and a "tail" vertex whose alpha is 0. Trail positions are not animated
 * on the CPU - each vertex only stores a normalized (parameter space) spawn
 * location plus two small attributes:
 *
 *   position : lateral xy inside a unit disc, z = 0..1 along the travel volume
 *   aSide    : 0 = head (alpha 1), 1 = tail (alpha 0)
 *   aBright  : per trail random brightness (a little variety)
 *
 * The vertex shader turns that into a world position from a single `uTime`
 * uniform, so the vertex buffer is uploaded to the GPU exactly once. Every frame
 * we write one float (uTime) and the GPU recycles the trails with a modulo.
 * Because `aSide` is interpolated between the head and the tail, the fragment
 * alpha fades linearly to 0 towards the tail for free.
 *
 * "density" (trails emitted per second) is a lifetime problem:
 *
 *   lifetime   = depth / |speed|      (seconds a trail stays in the volume)
 *   liveTrails = density * lifetime   (trails alive at any moment)
 *
 * so density only changes how many trails of the pre-allocated pool are drawn
 * (geometry.setDrawRange) — again no per-frame CPU work and no re-allocation.
 * Length, speed and the volume size are uniforms, so the pool never has to be
 * rebuilt while the user drags sliders.
 */

const VERTEX_SHADER = /* glsl */`
attribute float aSide;   // 0.0 = head of the trail (alpha 1), 1.0 = tail (alpha 0)
attribute float aBright; // per trail random brightness, 1.0 = full

uniform float uTime;
uniform float uSpan;      // depth of the travel volume in world units
uniform float uLength;    // length of a trail in world units
uniform float uSpeed;     // world units per second (absolute value)
uniform float uRadius;    // lateral radius of the emission disc
uniform float uFade;      // fraction of the volume used to fade trails in/out
uniform float uDirection; // +1.0 = travel towards -Z, -1.0 = travel towards +Z

varying float vAlpha;

void main() {
  // position is normalized: one unit of z is one full lap through the volume,
  // which keeps this math independent of uSpan / uRadius
  float progress = mod( position.z - uDirection * uSpeed * uTime / uSpan, 1.0 );

  // fade the trail in after it is recycled and out before it despawns, so the
  // wrap around never pops (the head alpha fades the whole trail)
  float fade = smoothstep( 0.0, uFade, progress ) * ( 1.0 - smoothstep( 1.0 - uFade, 1.0, progress ) );

  // linear alpha ramp: 1.0 at the head -> 0.0 at the tail
  vAlpha = fade * aBright * ( 1.0 - aSide );

  vec3 pos = position;
  pos.x *= uRadius;
  pos.y *= uRadius;
  pos.z = ( progress - 0.5 ) * uSpan + uDirection * uLength * aSide;

  gl_Position = projectionMatrix * modelViewMatrix * vec4( pos, 1.0 );
}
`;

const FRAGMENT_SHADER = /* glsl */`
uniform vec3 uColor;
uniform float uBrightness;

varying float vAlpha;

void main() {
  // additive blending in three multiplies by the source alpha, so vAlpha acts
  // as per trail intensity
  gl_FragColor = vec4( uColor * uBrightness, vAlpha );

  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export const STAR_TRAIL_DEFAULTS = {
  enabled: true,
  color: 0xffffff,
  length: 14,       // world units of a trail, fading to alpha 0 at the tail
  density: 50,     // trails emitted per second
  speed: 50,       // world units per second
  spread: 300,      // lateral radius of the emission disc
  depth: 900,       // depth of the travel volume (also sets the trail lifetime)
  brightness: 3.0,
  reverse: false,   // fly the other way (travel towards +Z)
  fade: 0.66,       // fraction of the volume used to fade trails in/out
  brightnessVariance: 1.0,
  pool: 16384,      // maximum number of simultaneously live trails
};

export default class StarTrails {

  constructor({
    scene,
    params = {},
    pool,
  } = {}) {
    this.scene = scene;
    this.params = Object.assign( {}, STAR_TRAIL_DEFAULTS, params );
    this.pool = Math.max( 2, Math.floor( pool || this.params.pool || STAR_TRAIL_DEFAULTS.pool ) );

    this.uniforms = {
      uTime:       { value: 0 },
      uColor:      { value: new THREE.Color( this.params.color ) },
      uSpan:       { value: Math.max( 1, this.params.depth ) },
      uLength:     { value: Math.max( 0, this.params.length ) },
      uSpeed:      { value: Math.max( 0.001, Math.abs( this.params.speed ) ) },
      uRadius:     { value: Math.max( 1, this.params.spread ) },
      uFade:       { value: Math.min( 0.5, Math.max( 0, this.params.fade ) ) },
      uBrightness: { value: this.params.brightness },
      uDirection:  { value: this.params.reverse ? -1.0 : 1.0 },
    };

    this.clamped = false;   // true when the density was limited by the pool size
    this.activeCount = 0;   // number of trails drawn right now

    this.geometry = this.createGeometry( this.pool );

    this.material = new THREE.ShaderMaterial( {
      uniforms: this.uniforms,
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true,
      toneMapped: true, // match the tone mapping of the rest of the scene
    } );

    this.mesh = new THREE.LineSegments( this.geometry, this.material );
    this.mesh.name = 'star_trails';
    this.mesh.frustumCulled = false;     // the real positions only exist in the vertex shader
    this.mesh.renderOrder = 10;          // draw after the ship
    this.mesh.visible = !!this.params.enabled;
    this.mesh.raycast = function () {};  // keep out of ship picking / STL export

    this.scene.add( this.mesh );

    this.setDensity( this.params.density ); // computes the initial draw range
  }

  /*
   * One line segment per trail: two vertices sharing a spawn point, one with
   * aSide 0 (head) and one with aSide 1 (tail). This is built once; everything
   * that can change at runtime is a uniform (or the draw range).
   */
  createGeometry( count ) {
    const positions = new Float32Array( count * 2 * 3 );
    const sides = new Float32Array( count * 2 );
    const brightness = new Float32Array( count * 2 );
    const variance = this.params.brightnessVariance;

    for ( let i = 0; i < count; i++ ) {
      // uniform density over the emitting disc (sqrt keeps the area distribution flat)
      const angle = Math.random() * Math.PI * 2.0;
      const radius = Math.sqrt( Math.random() );
      const x = Math.cos( angle ) * radius;
      const y = Math.sin( angle ) * radius;
      const z = Math.random();
      const b = 1.0 - variance * Math.random();

      const head = i * 2;
      const tail = head + 1;

      positions[ head * 3 + 0 ] = x;
      positions[ head * 3 + 1 ] = y;
      positions[ head * 3 + 2 ] = z;
      positions[ tail * 3 + 0 ] = x;
      positions[ tail * 3 + 1 ] = y;
      positions[ tail * 3 + 2 ] = z;

      sides[ head ] = 0.0;
      sides[ tail ] = 1.0;

      brightness[ head ] = b;
      brightness[ tail ] = b;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute( 'position', new THREE.BufferAttribute( positions, 3 ) );
    geometry.setAttribute( 'aSide', new THREE.BufferAttribute( sides, 1 ) );
    geometry.setAttribute( 'aBright', new THREE.BufferAttribute( brightness, 1 ) );

    // the real bounds only exist in the vertex shader, so hand three a generous sphere
    geometry.boundingSphere = new THREE.Sphere( new THREE.Vector3(), 1e6 );

    return geometry;
  }

  /*
   * density (trails per second) -> number of trails drawn, i.e. the draw range:
   * density * lifetime, capped by the pool size.
   */
  updateActiveCount() {
    const speed = Math.max( Math.abs( this.params.speed ), 0.001 );
    const lifetime = this.params.depth / speed;
    const wanted = this.params.density * lifetime;

    this.clamped = wanted > this.pool;
    this.activeCount = Math.min( Math.round( wanted ), this.pool );
    this.geometry.setDrawRange( 0, this.activeCount * 2 );

    return this.activeCount;
  }

  update( elapsedSeconds ) {
    if ( !this.params.enabled ) return;
    // the one and only per frame CPU write, the GPU does the rest
    this.uniforms.uTime.value = elapsedSeconds;
  }

  setEnabled( value ) {
    this.params.enabled = !!value;
    this.mesh.visible = this.params.enabled;
    return this;
  }

  setColor( value ) {
    this.params.color = value;
    this.uniforms.uColor.value.set( value );
    return this;
  }

  setLength( value ) {
    this.params.length = Math.max( 0, value );
    this.uniforms.uLength.value = this.params.length;
    return this;
  }

  setDensity( value ) {
    this.params.density = Math.max( 0, value );
    this.updateActiveCount();
    return this;
  }

  setSpeed( value ) {
    this.params.speed = value;
    this.uniforms.uSpeed.value = Math.max( 0.001, Math.abs( value ) );
    this.updateActiveCount();
    return this;
  }

  setSpread( value ) {
    this.params.spread = Math.max( 1, value );
    this.uniforms.uRadius.value = this.params.spread;
    return this;
  }

  setDepth( value ) {
    this.params.depth = Math.max( 1, value );
    this.uniforms.uSpan.value = this.params.depth;
    this.updateActiveCount();
    return this;
  }

  setBrightness( value ) {
    this.params.brightness = Math.max( 0, value );
    this.uniforms.uBrightness.value = this.params.brightness;
    return this;
  }

  setReverse( value ) {
    this.params.reverse = !!value;
    this.uniforms.uDirection.value = this.params.reverse ? -1.0 : 1.0;
    return this;
  }

  /*
   * Derived values, handy for the console and for tuning:
   * lifetime  = seconds a trail spends inside the volume
   * active    = trails drawn right now
   * requested = trails the current density/speed/depth would like to draw
   */
  getStats() {
    const speed = Math.max( Math.abs( this.params.speed ), 0.001 );
    const lifetime = this.params.depth / speed;
    return {
      pool: this.pool,
      active: this.activeCount,
      requested: Math.round( this.params.density * lifetime ),
      lifetime: lifetime,
      clamped: this.clamped,
    };
  }

  dispose() {
    this.scene.remove( this.mesh );
    this.geometry.dispose();
    this.material.dispose();
  }
}
